import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, interval } from 'rxjs';
import { takeUntil, finalize, switchMap } from 'rxjs/operators';

import { ClassesEntity } from '../../../../../../models/entities/classes-entity';
import { IClassesEntityService } from '../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../injection-tokens/entity-service-tokens';
import { TenantSecretService } from '../../../../../../services/tenant-secret.service';
import { TenantSecretDto, CreateTenantSecretRequest, TenantGroupDto } from '../../../../../../models/tenant-secret.model';
import { Log } from '../../../../../../services/logger.service';

@Component({
  selector: 'app-tenant-settings',
  templateUrl: './tenant-settings.component.html',
  styleUrls: ['./tenant-settings.component.css'],
})
export class TenantSettingsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() classEntity!: ClassesEntity;
  @Input() classId!: string;
  /** Emitted after a successful TenantSecret assignment so the parent can re-fetch classEntity. */
  @Output() tenantConfigured = new EventEmitter<void>();

  /** All TenantSecrets in the shared, global pool. */
  availableSecrets: TenantSecretDto[] = [];
  isLoadingSecrets = false;

  /** The TenantSecretId currently stored on the class (null = unassigned). */
  assignedSecretId: string | null = null;

  /** List of tenant groups from backend (grouped by tenantId) */
  tenantGroups: TenantGroupDto[] = [];
  selectedTenantId: string | null = null;

  /** The id selected in the dropdown (two-way bound). */
  selectedSecretId: string | null = null;
  isAssigning = false;

  /** Controls visibility of the inline-create panel. */
  showCreatePanel = false;
  createForm!: FormGroup;
  isCreating = false;
  showCreateSecret = false;

  private readonly logger = Log.create(TenantSettingsComponent);
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly tenantSecretService: TenantSecretService,
    private readonly snackBar: MatSnackBar,
  ) { }

  ngOnInit(): void {
    this.initCreateForm();
    this.loadSecrets();
  }

  private initCreateForm(): void {
    this.createForm = this.fb.group({
      label: ['', [Validators.required, Validators.maxLength(200)]],
      tenantId: ['', Validators.required],
      clientId: ['', Validators.required],
      clientSecret: ['', Validators.required],
    });
  }

  private updateAssignedSecretId(): void {
    const rawSecretId = this.classEntity?.tenantSecretId;
    
    // Check for null, undefined, empty, or invalid values
    if (!rawSecretId || 
        typeof rawSecretId !== 'string' || 
        rawSecretId.trim().length === 0 ||
        rawSecretId.trim().toLowerCase() === 'null' ||
        rawSecretId.trim().toLowerCase() === 'undefined' ||
        rawSecretId.trim() === '00000000-0000-0000-0000-000000000000') {
      this.assignedSecretId = null;
      return;
    }

    this.assignedSecretId = rawSecretId.trim();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['classEntity']) {
      // Trust the backend completely - read whatever TenantSecretId it has
      this.updateAssignedSecretId();

      // Reset dropdowns first
      this.selectedTenantId = null;
      this.selectedSecretId = null;

      // Only sync if we have secrets loaded AND there's actually an assigned secret
      if (this.availableSecrets.length > 0 && this.assignedSecretId) {
        this.syncDropdownSelections();
      }
    }
  }

  private syncDropdownSelections(): void {
    // Only pre-fill dropdowns if there's a valid assigned secret
    // For new/unassigned classes, keep dropdowns empty
    if (!this.assignedSecretId) {
      this.selectedTenantId = null;
      this.selectedSecretId = null;
      return;
    }

    const matchedSecret = this.assignedSecret;
    
    if (matchedSecret) {
      // Class IS assigned and secret exists in available list -> set dropdown values
      this.selectedTenantId = matchedSecret.tenantId;
      this.selectedSecretId = matchedSecret.id;
    } else {
      // assignedSecretId exists but not found in available secrets (edge case)
      this.selectedTenantId = null;
      this.selectedSecretId = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---- Helpers ----------------------------------------------------------------
  /** Helper to format Tenant label: "Tenant Name - Tenant ID" */
  getTenantDisplayLabel(tenant: TenantGroupDto): string {
    if (!tenant) return '';
    return tenant.tenantName ? `${tenant.tenantName} - ${tenant.tenantId}` : tenant.tenantId;
  }

  /** Helper to format Client label: "Client Name - Client ID" */
  getClientDisplayLabel(client: TenantSecretDto): string {
    if (!client) return '';
    const name = client.clientName || client.label;
    const id = client.clientId || client.id;
    return name ? `${name} - ${id}` : id;
  }
  /** The TenantSecretDto currently assigned to the class, or null if none. */
  get assignedSecret(): TenantSecretDto | null {
    // If no assignedSecretId or it's empty, definitely not assigned
    if (!this.assignedSecretId || this.assignedSecretId.trim().length === 0) {
      return null;
    }
    
    // Find the secret in the available list
    const found = this.availableSecrets.find(s => s.id === this.assignedSecretId);
    return found ?? null;
  }

  /**
   * True when the class already has one or more VM or ACC subscriptions attached.
   * Once true, the Tenant credential is locked (Rule 1: Tenant Lock) — changing the tenant
   * after subscriptions exist would invalidate provider registration and quota state already
   * verified against the current tenant.
   */
  get hasAnySubscriptions(): boolean {
    const vmSubs: any[] = this.classEntity?.data?.['VMSubscription'] ?? [];
    const accSubs: any[] = this.classEntity?.data?.['AccSubscription'] ?? [];
    return vmSubs.length > 0 || accSubs.length > 0;
  }

  /**
   * True when the dropdown should be locked: subscriptions exist AND a tenant is already
   * assigned (first-time assignment on a fresh class must remain possible even if this
   * getter is ever evaluated before assignedSecretId is set).
   */
  get isTenantLocked(): boolean {
    return this.hasAnySubscriptions && !!this.assignedSecretId;
  }

  /** Getter to fetch clients for whatever tenant is selected in Dropdown 1 */
  get clientsForSelectedTenant(): TenantSecretDto[] {
    if (!this.selectedTenantId) return [];
    const group = this.tenantGroups.find(g => g.tenantId === this.selectedTenantId);
    return group?.clients ?? [];
  }

  // ---- Load -------------------------------------------------------------------

  private loadSecrets(): void {
    this.isLoadingSecrets = true;
    this.tenantSecretService.getGrouped()
      .pipe(takeUntil(this.destroy$), finalize(() => this.isLoadingSecrets = false))
      .subscribe({
        next: (grouped) => {
          this.tenantGroups = grouped.tenants;
          // Flatten all clients for backward compatibility with assignedSecret lookup
          this.availableSecrets = grouped.tenants.flatMap(t => t.clients);
          this.syncDropdownSelections();
        },
        error: (err: any) => {
          this.logger.e('Failed to load tenant secrets', err);
          this.snackBar.open('Could not load tenant credentials', 'Dismiss', { duration: 3000 });
        },
      });
  }

  /**
   * `EnsureDisplayNamesResolvedAsync` runs fire-and-forget on the backend after a
   * successful assignment, so `TenantName`/`ClientName` are not yet populated in the
   * response we just got back. Poll `getGrouped()` every 2s (up to 15s / 8 attempts) until
   * the just-assigned secret has both names resolved, then stop — avoids requiring a
   * manual page reload to see the resolved names.
   */
  private pollForResolvedNames(secretId: string): void {
    let attempts = 0;
    const maxAttempts = 8;
    const subscription = interval(2000)
      .pipe(
        switchMap(() => this.tenantSecretService.getGrouped()),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (grouped) => {
          attempts++;
          this.tenantGroups = grouped.tenants;
          this.availableSecrets = grouped.tenants.flatMap(t => t.clients);
          
          const target = this.availableSecrets.find(s => s.id === secretId);
          const resolved = !!(target?.tenantName && target?.clientName);
          if (resolved || attempts >= maxAttempts) {
            subscription.unsubscribe();
          }
        },
        error: (err: any) => {
          this.logger.e('Polling for resolved tenant/client names failed', err);
          subscription.unsubscribe();
        },
      });
  }

  // ---- Assign existing --------------------------------------------------------

  /** Triggered when the user selects a Tenant from Dropdown 1 */
  onTenantChange(tenantId: string): void {
    this.selectedTenantId = tenantId;
    this.selectedSecretId = null; // Reset Dropdown 2 (Client) whenever Tenant changes
  }

  /** Called when the user picks a secret from the dropdown and clicks Assign. */
  assign(): void {
    if (!this.selectedSecretId || this.selectedSecretId === this.assignedSecretId) return;
    
    this.isAssigning = true;
    this.classService.assignTenantSecret(this.classId, this.selectedSecretId)
      .pipe(takeUntil(this.destroy$), finalize(() => this.isAssigning = false))
      .subscribe({
        next: () => {
          this.assignedSecretId = this.selectedSecretId;
          this.snackBar.open('Tenant credential assigned', 'Dismiss', { duration: 2500, panelClass: 'snack-success' });
          this.logger.i('TenantSecret assigned', this.selectedSecretId);
          this.tenantConfigured.emit();
          this.pollForResolvedNames(this.selectedSecretId!);
        },
        error: (err: any) => {
          this.logger.e('Assign tenant secret error', err);
          this.snackBar.open('Failed to assign tenant credential', 'Dismiss', { duration: 3000, panelClass: 'snack-error' });
        },
      });
  }

  // ---- Inline create ----------------------------------------------------------

  toggleCreatePanel(): void {
    this.showCreatePanel = !this.showCreatePanel;
    if (!this.showCreatePanel) {
      this.createForm.reset();
    }
  }

  createAndAssign(): void {
    if (this.createForm.invalid) return;
    this.isCreating = true;

    const rawValue = this.createForm.getRawValue();
    const request: CreateTenantSecretRequest = {
      label: rawValue.label?.trim() ?? '',
      tenantId: rawValue.tenantId?.trim() ?? '',
      clientId: rawValue.clientId?.trim() ?? '',
      clientSecret: rawValue.clientSecret?.trim() ?? '',
    };

    this.tenantSecretService.create(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (created) => {
          // Add to availableSecrets and rebuild groups
          this.availableSecrets = [...this.availableSecrets, created];
          
          // Find or create tenant group
          let group = this.tenantGroups.find(g => g.tenantId === created.tenantId);
          if (group) {
            group.clients.push(created);
          } else {
            this.tenantGroups.push({
              tenantId: created.tenantId,
              tenantName: created.tenantName,
              clients: [created]
            });
          }

          this.selectedTenantId = created.tenantId;
          this.selectedSecretId = created.id;
          
          // Immediately assign the newly created secret to the class.
          this.classService.assignTenantSecret(this.classId, created.id)
            .pipe(takeUntil(this.destroy$), finalize(() => this.isCreating = false))
            .subscribe({
              next: () => {
                this.assignedSecretId = created.id;
                this.showCreatePanel = false;
                this.createForm.reset();
                this.snackBar.open('Tenant credential created and assigned', 'Dismiss', { duration: 2500, panelClass: 'snack-success' });
                this.logger.i('TenantSecret created and assigned', created.id);
                this.tenantConfigured.emit();
                this.pollForResolvedNames(created.id);
              },
              error: (err: any) => {
                this.isCreating = false;
                this.logger.e('Auto-assign after create failed', err);
                this.snackBar.open('Credential created but auto-assign failed — select it from the dropdown and assign manually.', 'Dismiss', { duration: 5000, panelClass: 'snack-error' });
              },
            });
        },
        error: (err: HttpErrorResponse) => {
          this.isCreating = false;
          this.logger.e('Create tenant secret error', err);
          const backendMessage = err?.error?.error;
          const message = typeof backendMessage === 'string' && backendMessage.trim()
            ? backendMessage
            : 'Failed to create tenant credential';
          this.snackBar.open(message, 'Dismiss', { duration: 5000, panelClass: 'snack-error' });
        },
      });
  }
}

